import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter } from 'react-router-dom';
import _ from 'lodash';

import GatherInfoContextsIntro from './GatherInfoContextsIntro';
import GatherInfoContextsEnvironments from './GatherInfoContextsEnvironments';
import GatherInfoContextsPositions from './GatherInfoContextsPositions';
import GatherInfoContextsIndependence from './GatherInfoContextsIndependence';
import GatherInfoContextsReview from './GatherInfoContextsReview';
import { SUBTABS, CONTEXTS_STEPS } from './ConstPaths';
import { isActivityLowTech } from './ConstActivityBtns';
import { findOtherIndex, getOtherEntryFromArray, OTHER_CKBX_VAL, OTHER_DISPLAY_SEP } from './OtherCheckboxUtils';

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  priority: PropTypes.string,
  environments: PropTypes.arrayOf(PropTypes.string),
  bodyPositions: PropTypes.arrayOf(PropTypes.string),
  indepSetup: PropTypes.string,
  indepUse: PropTypes.string,
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  priority: '',
  environments: [],
  bodyPositions: [],
  indepSetup: '',
  indepUse: ''
};

//Manage the wizard form for the Gather Info / Contexts sub-tab
//Initialize the forms with props and manage those as local state.
//On Continue, update the global Session state with the local state from the forms. 
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/

//the steps of the wizard form:
const steps = CONTEXTS_STEPS;

const BASE_URL = SUBTABS.CONTEXTS + "/";
const NEXT_SECTION = SUBTABS.HISTORY;

class GatherInfoContexts extends React.Component {
  constructor(props) {
    super(props);

    //manage the incoming values as local state
    this.state = {
      priority: this.props.priority, //read-only, not changed by this wizard section
      environments: this.props.environments ?? [],
      bodyPositions: this.props.bodyPositions ?? [],
      indepSetup: this.props.indepSetup,
      indepUse: this.props.indepUse,
      otherEnvironment: getOtherEntryFromArray(this.props.environments),
      otherBodyPosition: getOtherEntryFromArray(this.props.bodyPositions)
    };

    this.saveEnvironmentsToSession = this.saveEnvironmentsToSession.bind(this);
    this.savePositionsToSession = this.savePositionsToSession.bind(this);
    this.saveIndependenceToSession = this.saveIndependenceToSession.bind(this);

    this.onContinue = this.onContinue.bind(this);
  }

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/gather-info/contexts/intro' would have step of 'intro'
    //props.match: {"step":"intro"}
    const stepParam = this.props.match.params.step;
    if(stepParam){
      // console.log("GatherInfoContexts.getCurrentStep: got step from URL");
      return(stepParam);
    }
    else{
      // console.log("GatherInfoContexts.getCurrentStep: using default step");
      return(steps.INTRO);
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    let location = this.props.location;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
      const curURL = new URL(window.location.href);


    switch(this.getCurrentStep()){
      case steps.INTRO:
        nextStep = steps.ENVIRONMENTS;
        break;
      case steps.ENVIRONMENTS:
        nextStep = steps.POSITIONS;
        break;
      case steps.POSITIONS:
        //don't ask about independence if priority activity js low-tech
        if (isActivityLowTech(this.state.priority)){
          nextStep = steps.REVIEW;
        }
        else {
          nextStep = steps.INDEPENDENCE;
        }
        break;
      case steps.INDEPENDENCE:
        nextStep = steps.REVIEW;
        break;
      case steps.REVIEW:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.ENVIRONMENTS;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  saveEnvironmentsToSession(){
    this.props.updateSession('environments', this.state.environments);
  }
  savePositionsToSession(){
    this.props.updateSession('bodyPositions', this.state.bodyPositions);  
  }
  saveIndependenceToSession(){
    this.props.updateSession('indepSetup', this.state.indepSetup);
    this.props.updateSession('indepUse', this.state.indepUse);
  }

  //the keys are the names of the local state variables, defined above in the constructor
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;
      //TODO make this special handling a function, and use for 'activities' in Goals/Needs, too
      //handle 'environments' specially, since that holds an array rather than a single value
      if(key === 'environments'){
        const newItem=target.value;
        const newItemIndex = this.state.environments.findIndex((env) => env.startsWith(newItem));
        let environments = [...this.state.environments];
        //if an activity is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          environments.push(newItem);
        }
        //activity unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          environments.splice(newItemIndex, 1);
          if( newItem === OTHER_CKBX_VAL)
            this.setState({ 'otherEnvironment': ""});
        }
        value = environments;
      }
      //handle 'bodyPositions' specially, since that holds an array rather than a single value
      if(key === 'bodyPositions'){
        const newItem=target.value;
        const newItemIndex = this.state.bodyPositions.findIndex((bp) => bp.startsWith(newItem));
        let bodyPositions = [...this.state.bodyPositions];
        //if an activity is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          bodyPositions.push(newItem);
        }
        //activity unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          bodyPositions.splice(newItemIndex, 1);
          if( newItem === OTHER_CKBX_VAL)
            this.setState({ 'otherBodyPosition': ""});
        }
        value = bodyPositions;
      }

      console.log("Updating GatherInfoContexts state with key: " + key + " value: " + value);
      if (key === 'otherEnvironment')
        this.setState({ [key]: value }, this.adjustEnvironments);
      else if (key === 'otherBodyPosition')
        this.setState({ [key]: value }, this.adjustBodyPositions);
      else this.setState({ [key]: value });
    }
  }

  adjustEnvironments(){
    let otherEnvironment = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherEnvironment;
    let environments = [...this.state.environments];
    const otherItemIndex = findOtherIndex(this.state.environments);
    if( otherItemIndex > -1){
      environments[otherItemIndex] = otherEnvironment;
      this.setState({ 'environments': environments});
    }
  }
  adjustBodyPositions(){
    let otherBodyPosition = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherBodyPosition;
    let bodyPositions = [...this.state.bodyPositions];
    const otherItemIndex = findOtherIndex(this.state.bodyPositions);
    if( otherItemIndex > -1){
      bodyPositions[otherItemIndex] = otherBodyPosition;
      this.setState({ 'bodyPositions': bodyPositions});
    }
  }

  componentDidUpdate(prevProps, prevState){
    //for values that are Arrays:
    if (_.isEqual(this.props.environments, prevProps.environments) === false){
      console.log("--incoming props: " + this.props.environments + " differ from prev props: " + prevProps.environments);
      this.setState({'environments': this.props.environments});
    };
    if (_.isEqual(this.props.bodyPositions, prevProps.bodyPositions) === false){
      console.log("--incoming props: " + this.props.bodyPositions + " differ from prev props: " + prevProps.bodyPositions);
      this.setState({'bodyPositions': this.props.bodyPositions});
    };
    //for simple strings
    if (this.props.indepSetup !== prevProps.indepSetup){
        console.log("--incoming props: " + this.props.indepSetup + " differ from prev props: " + prevProps.indepSetup);
        this.setState({'indepSetup': this.props.indepSetup});
    }
    if (this.props.indepUse !== prevProps.indepUse){
      console.log("--incoming props: " + this.props.indepUse + " differ from prev props: " + prevProps.indepUse);
      this.setState({'indepUse': this.props.indepUse});
    }
  }

  render() {
    console.log("rendering GatherInfoContexts, with props: " + JSON.stringify(this.props));
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;

    return(
    <>
    <Switch>
      <Route path={BASE_URL+ steps.INTRO}>
        <GatherInfoContextsIntro
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
            />
      </Route>
      <Route path={BASE_URL + steps.ENVIRONMENTS}>
        <GatherInfoContextsEnvironments
              key={steps.ENVIRONMENTS}
              firstName={firstName}
              // pronouns={pronouns}
              onContinue={this.onContinue}
              environments={this.state.environments}
              onChangeEnvironments={this.updateLocalState("environments")}
              otherEnvironment={this.state.otherEnvironment}
              onChangeOtherEnvironment={this.updateLocalState("otherEnvironment")}
              saveFormData={this.saveEnvironmentsToSession}
            />
      </Route>
      <Route path={BASE_URL + steps.POSITIONS}>
        <GatherInfoContextsPositions
              key={steps.POSITIONS}
              firstName={firstName}
              // pronouns={pronouns}
              onContinue={this.onContinue}
              bodyPositions={this.state.bodyPositions}
              onChangeBodyPositions={this.updateLocalState("bodyPositions")}
              otherBodyPosition={this.state.otherBodyPosition}
              onChangeOtherBodyPosition={this.updateLocalState("otherBodyPosition")}
              saveFormData={this.savePositionsToSession}
            />
      </Route>
      <Route path={BASE_URL + steps.INDEPENDENCE}>
        <GatherInfoContextsIndependence
              key={steps.INDEPENDENCE}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
              indepSetup={this.state.indepSetup}
              indepUse={this.state.indepUse}
              onChangeIndepSetup={this.updateLocalState("indepSetup")}
              onChangeIndepUse={this.updateLocalState("indepUse")}
              saveFormData={this.saveIndependenceToSession}
            />
    </Route>
      <Route path={BASE_URL + steps.REVIEW}>
        <GatherInfoContextsReview
              key={steps.REVIEW}
              firstName={firstName}
              // pronouns={pronouns}
              onContinue={this.onContinue}
              environments={this.state.environments}
              bodyPositions={this.state.bodyPositions}
              indepSetup={this.state.indepSetup}
              indepUse={this.state.indepUse}
            />
      </Route>
      <Route path={BASE_URL}>
        <GatherInfoContextsIntro
              key={steps.INTRO}
              firstName={firstName}
              pronouns={pronouns}
              onContinue={this.onContinue}
            />
      </Route>
    </Switch>
    </>
    );
  }
}

GatherInfoContexts.propTypes = propTypes;
GatherInfoContexts.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props (used in onContinue)
export default withRouter(GatherInfoContexts);
